import { Component, OnInit } from '@angular/core';

import { SplashScreen } from '@awesome-cordova-plugins/splash-screen/ngx';
import { StatusBar } from '@awesome-cordova-plugins/status-bar/ngx';
import { AlertController, LoadingController, Platform } from '@ionic/angular';

import { Title } from '@angular/platform-browser';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { Network } from '@awesome-cordova-plugins/network/ngx';
import { Deploy } from 'cordova-plugin-ionic';
import { EMPTY, ReplaySubject, firstValueFrom, from } from 'rxjs';
import { catchError, filter, map, mergeMap, startWith, switchMap, take, tap, timeout } from 'rxjs/operators';
import { RemoteConfigService } from 'src/app/core/services/remote-config.service';
import { environment } from 'src/environments/environment';
import './shared/angular-formcontrols-extension';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
})
export class AppComponent implements OnInit {
  resumeSubscribe: any;

  channel: string = environment.channel;

  updateInProgress = false;

  loader: HTMLIonLoadingElement | undefined;

  // updated by watchNetworkConnection()
  isNetworkConnected = false;

  // Alert reference for network disconnection
  networkAlert: HTMLIonAlertElement | null | undefined;

  // Controls JavaScript driven updates of the app.
  isJavaScriptUpdateEnabled: ReplaySubject<boolean> = new ReplaySubject(1);

  constructor(
    private platform: Platform,
    private splashScreen: SplashScreen,
    private statusBar: StatusBar,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private titleService: Title,
    private loadingCtrl: LoadingController,
    private network: Network,
    private alertCtrl: AlertController,
    private remoteConfigService: RemoteConfigService,
  ) {}

  /**
   * Removes the div containing loader from the DOM.
   */
  hideLoader(): void {
    const appLoader = document.querySelector('.app-loader');
    if (appLoader) {
      console.log('[Core] App activated on startup');
      appLoader.remove();
    }
  }

  ngOnInit() {
    let previousUrl = '';
    let currentUrl = '';

    this.initializeApp();

    // https://ultimatecourses.com/blog/dynamic-page-titles-angular-2-router-events
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        tap((val) => {
          previousUrl = currentUrl;
          currentUrl = val['url'];

          // store the previous URL in local storage so that if the user refreshes the page, we can still get their previous route
          if (previousUrl && previousUrl !== localStorage.getItem('previousUrl')) {
            localStorage.setItem('previousUrl', JSON.stringify(previousUrl));
          }
        }),
        map(() => this.activatedRoute),
        map((route) => {
          while (route.firstChild) {
            route = route.firstChild;
          }
          return route;
        }),
        filter((route) => route.outlet === 'primary'),
        mergeMap((route) => route.data),
      )
      .subscribe((data: any) => {
        // console.log(data);
        this.titleService.setTitle(data.title);
      });
  }

  initializeApp() {
    this.platform.ready().then(() => {
      // Lets configure our deployment settings with remote config.
      const deployConfiguration$ = from(Deploy.getConfiguration()).pipe(
        tap((currentConfig) => this.isJavaScriptUpdateEnabled.next(currentConfig.updateMethod === 'none')),
        catchError((error) => {
          this.isJavaScriptUpdateEnabled.next(true);
          console.error('[Deploy] was unable to retrieve config:', error);
          return EMPTY;
        }),
      );

      deployConfiguration$
        .pipe(
          switchMap(() => this.remoteConfigService.remoteConfig$.pipe(take(1), timeout({ first: 60000 }))),
          catchError((error) => {
            console.error('[Remote Config] request failed with error:', error);
            return EMPTY;
          }),
        )
        .subscribe((remoteConfig) => {
          Deploy.configure({
            updateMethod: remoteConfig.ionicPluginUpdateMethod,
            minBackgroundDuration: Number(remoteConfig.ionicPluginMinBackgroundDuration),
          }).then(() => console.log('Deploy Config Applied'));
        });

      // let status bar overlay webview
      this.statusBar.overlaysWebView(false);

      // set status bar to white
      this.statusBar.backgroundColorByHexString('#ffffff');

      this.statusBar.styleDefault();
      this.splashScreen.hide();
      this.startApp();
    });
  }

  startApp() {
    if (!this.resumeSubscribe) {
      this.resumeSubscribe = this.platform.resume.pipe(startWith(1)).subscribe(() => {
        this.checkForcedAppUpdates().then((canLiveUpdate) => (canLiveUpdate ? this.performLiveUpdate() : null));
      });
    }

    this.watchNetworkConnection();
  }

  /**
   * We want to disable application interaction when network connection is unavailable.
   * This method will watch network status and handle hiding/showing the alert to disable interaction.
   */
  async watchNetworkConnection() {
    // watch for disconnection. On disconnect, present the modal
    // NOTE: no need to unsubscribe from these since it's done at app.component.ts (only destroyed on app close)
    this.network.onDisconnect().subscribe(async () => {
      if (!this.networkAlert) {
        this.networkAlert = await this.alertCtrl.create({
          header: 'Connection lost',
          message: 'No internet connection. Check your data or wifi connection.',
          backdropDismiss: false,
        });
        await this.networkAlert.present();
      }
      this.isNetworkConnected = false;
    });

    // watch network for a connection
    this.network.onConnect().subscribe(() => {
      this.isNetworkConnected = true;
      // Per ionic docs: Even once we get a connection - we need to wait briefly
      // prior to doing any api requests. So wait to dismiss the alert.
      setTimeout(async () => {
        // checking this.isNetworkConnected, otherwise if the internet temporarily connects and then disconnects again
        // because of the timeout we would have dismissed the connection modal incorrectly.
        if (this.isNetworkConnected && this.networkAlert) {
          await this.networkAlert.dismiss();
          this.networkAlert = null;
        }
      }, 3000);
    });
  }

  async performLiveUpdate() {
    if (!this.platform.is('cordova')) {
      return;
    }

    const isJavaScriptUpdateEnabled = await firstValueFrom(this.isJavaScriptUpdateEnabled);

    if (!isJavaScriptUpdateEnabled) {
      return;
    }

    await Deploy.configure({ channel: this.channel });

    const update = await Deploy.checkForUpdate();

    if (!this.updateInProgress && update.available) {
      this.updateInProgress = true;
      this.presentLiveLoading();

      // can give user feedback with progress if we want to
      // give user visual feedback on the download and install progress
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      await Deploy.downloadUpdate((progress) => {
        if (this.loader && progress) {
          this.loader.message = `Keep app open to download updates. ${100 - progress}% remaining...`;
        }
      });
      await Deploy.extractUpdate((progress) => {
        if (this.loader && progress) {
          this.loader.message = `Installing updates. ${100 - progress}% remaining...`;
        }
        if (progress === 100) {
          this.updateInProgress = false;
        }
      });
      await Deploy.reloadApp();
    }
  }

  async presentLiveLoading() {
    this.loader = await this.loadingCtrl.create({
      message: 'Updating Please wait...',
    });
    return await this.loader.present();
  }

  /**
   * Checks for the latest binary required from firebase remote config.
   */
  private async checkForcedAppUpdates(): Promise<boolean> {
    return true;
  }
}
